<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStackZapOnboarding } from 'dashboard/routes/dashboard/voipCalls/composables/useStackZapOnboarding';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const inboxId = computed(() => props.inbox?.id);

const {
  wizardState,
  device,
  qrImage,
  providerStatus,
  errorMessage,
  errorCode,
  isBusy,
  technicalOpen,
  manualConfigOpen,
  bootstrapApiKey,
  lastTestResult,
  phoneMasked,
  inboxName,
  lastSeenAt,
  loadExistingDevice,
  startSetup,
  regenerateQr,
  reconnect,
  disconnect,
  testConnection,
  cleanup,
} = useStackZapOnboarding({ inboxId });

onMounted(() => {
  if (inboxId.value) loadExistingDevice();
});

watch(inboxId, (id, prev) => {
  cleanup();
  if (id) loadExistingDevice();
  else if (prev) {
    /* inbox cleared */
  }
});

function statusLabel(status) {
  if (!status) return '—';
  const statusMap = {
    connected: t('VOIP_DEVICES.WIZARD.STATUS.connected'),
    connecting: t('VOIP_DEVICES.WIZARD.STATUS.connecting'),
    qr_pending: t('VOIP_DEVICES.WIZARD.STATUS.qr_pending'),
    pending: t('VOIP_DEVICES.WIZARD.STATUS.pending'),
    disconnected: t('VOIP_DEVICES.WIZARD.STATUS.disconnected'),
    logged_out: t('VOIP_DEVICES.WIZARD.STATUS.logged_out'),
    error: t('VOIP_DEVICES.WIZARD.STATUS.error'),
  };
  return statusMap[status] || status;
}

const displayStatus = computed(() => {
  return (
    providerStatus.value?.provider_status ||
    providerStatus.value?.status ||
    device.value?.status ||
    ''
  );
});

const integrationConfigured = computed(() => {
  // Exact contract fields only — no api_key_present / server_api_key_present.
  return !!(
    device.value?.stackzap_configured ||
    providerStatus.value?.stackzap_configured
  );
});

const eventsConfigured = computed(() => {
  return !!(
    device.value?.stackzap_webhook_configured ||
    providerStatus.value?.stackzap_webhook_configured
  );
});
</script>

<template>
  <div class="flex flex-col gap-5 max-w-2xl">
    <header class="space-y-1">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('VOIP_DEVICES.WIZARD.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('VOIP_DEVICES.WIZARD.SUBTITLE') }}
      </p>
    </header>

    <!-- Estado 0: sem conexão -->
    <section
      v-if="wizardState === 'idle'"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 space-y-4"
    >
      <div class="flex items-start gap-3">
        <span class="i-lucide-phone-call size-6 text-n-brand mt-0.5" />
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('VOIP_DEVICES.WIZARD.IDLE_HEADING') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('VOIP_DEVICES.WIZARD.IDLE_BODY') }}
          </p>
        </div>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-xl bg-n-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-n-brand/90 disabled:opacity-50"
        :disabled="isBusy"
        @click="startSetup"
      >
        <span v-if="isBusy" class="i-ri-loader-2-line size-4 animate-spin" />
        {{ t('VOIP_DEVICES.WIZARD.START') }}
      </button>

      <!-- Configuração manual (legado) — colapsada, fora da jornada padrão -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-2.5 text-sm text-n-slate-11 hover:bg-n-alpha-1"
          @click="manualConfigOpen = !manualConfigOpen"
        >
          <span>{{ t('VOIP_DEVICES.WIZARD.MANUAL_TITLE') }}</span>
          <span
            class="i-ri-arrow-down-s-line size-4 transition-transform"
            :class="{ 'rotate-180': manualConfigOpen }"
          />
        </button>
        <div
          v-if="manualConfigOpen"
          class="px-4 pb-4 space-y-2 border-t border-n-weak pt-3"
        >
          <p class="text-xs text-n-slate-11">
            {{ t('VOIP_DEVICES.WIZARD.MANUAL_HINT') }}
          </p>
          <label class="block text-xs font-medium text-n-slate-11">
            {{ t('VOIP_DEVICES.WIZARD.INTEGRATION_KEY') }}
            <input
              v-model="bootstrapApiKey"
              type="password"
              autocomplete="off"
              class="mt-1 w-full rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
              :placeholder="
                t('VOIP_DEVICES.WIZARD.INTEGRATION_KEY_PLACEHOLDER')
              "
            />
          </label>
        </div>
      </div>
    </section>

    <!-- Estado 1: provisionando -->
    <section
      v-else-if="wizardState === 'preparing'"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-8 text-center space-y-3"
    >
      <span
        class="inline-flex i-ri-loader-2-line size-10 animate-spin text-n-brand"
      />
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('VOIP_DEVICES.WIZARD.PREPARING_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 max-w-md mx-auto">
        {{ t('VOIP_DEVICES.WIZARD.PREPARING_BODY') }}
      </p>
    </section>

    <!-- Estado 2: QR -->
    <section
      v-else-if="wizardState === 'qr'"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 space-y-4"
    >
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('VOIP_DEVICES.WIZARD.QR_TITLE') }}
        </h3>
        <ol
          class="mt-2 space-y-1 text-sm text-n-slate-11 list-decimal list-inside"
        >
          <li>{{ t('VOIP_DEVICES.WIZARD.QR_STEP_1') }}</li>
          <li>
            {{ t('VOIP_DEVICES.WIZARD.QR_STEP_2_PREFIX') }}
            <strong>{{ t('VOIP_DEVICES.WIZARD.QR_STEP_2_LINKED') }}</strong>
            {{ t('VOIP_DEVICES.WIZARD.QR_STEP_2_MID') }}
            <strong>{{ t('VOIP_DEVICES.WIZARD.QR_STEP_2_CONNECT') }}</strong>
          </li>
          <li>{{ t('VOIP_DEVICES.WIZARD.QR_STEP_3') }}</li>
        </ol>
        <p class="mt-2 text-xs text-n-amber-11">
          {{ t('VOIP_DEVICES.WIZARD.QR_TEST_HINT') }}
        </p>
      </div>

      <div class="flex flex-col sm:flex-row items-center gap-4">
        <div
          class="size-56 rounded-2xl border border-n-weak bg-white p-2 flex items-center justify-center"
        >
          <img
            v-if="qrImage"
            :src="qrImage"
            :alt="t('VOIP_DEVICES.WIZARD.QR_ALT')"
            class="size-full object-contain"
          />
          <span
            v-else
            class="i-ri-loader-2-line size-8 animate-spin text-n-slate-10"
          />
        </div>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
            :disabled="isBusy"
            @click="regenerateQr"
          >
            {{ t('VOIP_DEVICES.WIZARD.QR_REGENERATE') }}
          </button>
          <p class="text-xs text-n-slate-11">
            {{ t('VOIP_DEVICES.WIZARD.QR_STATUS_HINT') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Estado 3: conectado -->
    <section
      v-else-if="wizardState === 'connected'"
      class="rounded-2xl border border-n-teal-6 bg-n-teal-2/40 p-5 space-y-4"
    >
      <div class="flex items-start gap-3">
        <span class="i-ri-checkbox-circle-line size-6 text-n-teal-11 mt-0.5" />
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('VOIP_DEVICES.WIZARD.CONNECTED_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            <span class="font-medium text-n-slate-12">{{
              inboxName || inbox.name
            }}</span>
            <span class="mx-1" aria-hidden="true">{{ '·' }}</span>
            {{
              t('VOIP_DEVICES.WIZARD.CONNECTED_PHONE', { phone: phoneMasked })
            }}
          </p>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('VOIP_DEVICES.WIZARD.MESSAGES_LABEL') }}
            {{ statusLabel(displayStatus) }}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-xl bg-n-brand px-3 py-2 text-sm font-medium text-white hover:bg-n-brand/90 disabled:opacity-50"
          :disabled="isBusy"
          @click="testConnection"
        >
          {{ t('VOIP_DEVICES.WIZARD.TEST') }}
        </button>
        <button
          type="button"
          class="rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
          :disabled="isBusy"
          @click="reconnect"
        >
          {{ t('VOIP_DEVICES.WIZARD.RECONNECT') }}
        </button>
        <button
          type="button"
          class="rounded-xl border border-n-ruby-6 px-3 py-2 text-sm text-n-ruby-11 hover:bg-n-ruby-2 disabled:opacity-50"
          :disabled="isBusy"
          @click="disconnect"
        >
          {{ t('VOIP_DEVICES.WIZARD.DISCONNECT') }}
        </button>
      </div>

      <p
        v-if="lastTestResult"
        class="text-xs"
        :class="
          lastTestResult.ok === false ? 'text-n-ruby-11' : 'text-n-teal-11'
        "
      >
        {{
          lastTestResult.message ||
          (lastTestResult.ok === false
            ? t('VOIP_DEVICES.WIZARD.TEST_FAIL')
            : t('VOIP_DEVICES.WIZARD.TEST_OK'))
        }}
      </p>
    </section>

    <!-- Estado erro / soft QR recovery -->
    <section
      v-else-if="wizardState === 'error'"
      class="rounded-2xl border border-n-ruby-6 bg-n-ruby-2 p-5 space-y-3"
    >
      <div class="flex items-start gap-3">
        <span class="i-ri-alert-line size-6 text-n-ruby-11 mt-0.5" />
        <div>
          <h3 class="text-base font-semibold text-n-ruby-11">
            {{ t('VOIP_DEVICES.WIZARD.ERROR_TITLE') }}
          </h3>
          <p class="text-sm text-n-ruby-11 mt-1">{{ errorMessage }}</p>
        </div>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-xl bg-n-brand px-3 py-2 text-sm font-medium text-white hover:bg-n-brand/90"
          :disabled="isBusy"
          @click="device?.id ? regenerateQr() : startSetup()"
        >
          {{
            errorCode === 'qr_expired' || errorCode === 'qr_unavailable'
              ? t('VOIP_DEVICES.WIZARD.QR_REGENERATE')
              : t('VOIP_DEVICES.WIZARD.RETRY')
          }}
        </button>
        <button
          v-if="device?.id"
          type="button"
          class="rounded-xl border border-n-weak px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1"
          :disabled="isBusy"
          @click="reconnect"
        >
          {{ t('VOIP_DEVICES.WIZARD.RECONNECT') }}
        </button>
      </div>
    </section>

    <!-- Detalhes técnicos (colapsado; nunca mostra segredo; jargão neutro) -->
    <section
      v-if="device || errorCode"
      class="rounded-2xl border border-n-weak bg-n-solid-1 overflow-hidden"
    >
      <button
        type="button"
        class="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
        @click="technicalOpen = !technicalOpen"
      >
        <span>{{ t('VOIP_DEVICES.WIZARD.TECHNICAL_TITLE') }}</span>
        <span
          class="i-ri-arrow-down-s-line size-4 text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': technicalOpen }"
        />
      </button>
      <div
        v-if="technicalOpen"
        class="px-4 pb-4 space-y-2 text-sm border-t border-n-weak pt-3"
      >
        <div v-if="device" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_CONNECTION_ID')
          }}</span>
          <span class="text-n-slate-12 font-mono text-xs break-all">
            {{ device.stackzap_instance_id || '—' }}
          </span>
        </div>
        <div v-if="device" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_INTEGRATION')
          }}</span>
          <span class="text-n-slate-12">
            {{
              integrationConfigured
                ? t('VOIP_DEVICES.WIZARD.TECH_INTEGRATION_OK')
                : t('VOIP_DEVICES.WIZARD.TECH_INTEGRATION_MISSING')
            }}
          </span>
        </div>
        <div v-if="device" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_EVENTS')
          }}</span>
          <span class="text-n-slate-12">
            {{
              eventsConfigured
                ? t('VOIP_DEVICES.WIZARD.TECH_EVENTS_OK')
                : t('VOIP_DEVICES.WIZARD.TECH_EVENTS_PENDING')
            }}
          </span>
        </div>
        <div v-if="device" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_LAST_SEEN')
          }}</span>
          <span class="text-n-slate-12 text-xs">
            {{
              lastSeenAt ? new Date(lastSeenAt).toLocaleString('pt-BR') : '—'
            }}
          </span>
        </div>
        <div v-if="device" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_STATUS')
          }}</span>
          <span class="text-n-slate-12">{{
            statusLabel(displayStatus || device.status)
          }}</span>
        </div>
        <div v-if="errorCode" class="flex justify-between gap-4">
          <span class="text-n-slate-11">{{
            t('VOIP_DEVICES.WIZARD.TECH_ERROR_CODE')
          }}</span>
          <span class="text-n-slate-12 font-mono text-xs">{{ errorCode }}</span>
        </div>
      </div>
    </section>
  </div>
</template>
